import { DAY, isNotDefined, MINUTE, type ProgramTaskData, type ProgramTaskExpiration } from '@terros/common'
import type { SelectItem } from '../../types'
import { minutesToTime, timeToMinutes } from './duration'
import { type FormProgramTask, type TaskFormExpiration } from './types'

const MINUTES_PER_DAY = DAY / MINUTE

const DEFAULT_EXPIRATION_OPTIONS = ['never', 'endOfDay', 'programEndDate', '3days', 'week', 'custom'] as const
export type FormExpirationOption = (typeof DEFAULT_EXPIRATION_OPTIONS)[number]

export const EXPIRATION_OPTIONS: SelectItem<FormExpirationOption>[] = [
  { value: 'never', label: 'Never expires' },
  { value: 'endOfDay', label: 'End of day' },
  { value: '3days', label: '3 days after due date' },
  { value: 'week', label: '1 week after due date' },
  { value: 'programEndDate', label: 'At program end' },
  { value: 'custom', label: 'Custom' },
]

export function getTaskExpirationOptions(values: FormProgramTask): SelectItem<FormExpirationOption>[] {
  if (values.duration.type === 'custom') {
    const { days, time } = values.duration
    if (days !== 0) return getExpirationWithoutEndOfDay()
    return EXPIRATION_OPTIONS
  }

  return getExpirationWithoutEndOfDay()
}

const getExpirationWithoutEndOfDay = (): SelectItem<FormExpirationOption>[] => {
  return EXPIRATION_OPTIONS.filter((option) => option.value !== 'endOfDay')
}

export const fromFormExpiration = (
  expiration: TaskFormExpiration,
  duration = 0,
  openSince = 0
): ProgramTaskExpiration => {
  const { days = 0, time, type, enabled } = expiration

  if (!enabled) return 0

  switch (type) {
    case 'never':
      return undefined
    case 'programEndDate':
      return 'programEndDate'
    case '3days':
      return 3 * MINUTES_PER_DAY
    case 'week':
      return 7 * MINUTES_PER_DAY
    case 'endOfDay':
      return MINUTES_PER_DAY - duration - openSince
    case 'custom':
      const minutes = timeToMinutes(time) ?? 0
      const totalMinutes = days * MINUTES_PER_DAY + minutes
      const durationMinutes = duration % MINUTES_PER_DAY

      return totalMinutes - openSince - durationMinutes
  }
}

export const toFormExpiration = (
  task: Pick<ProgramTaskData, 'expiration' | 'openSince' | 'duration'>
): TaskFormExpiration => {
  const { expiration, openSince = 0, duration = 0 } = task
  if (expiration === 0) return { type: 'never', enabled: false, days: 0, time: '00:00' }

  if (isNotDefined(expiration)) return { type: 'never', enabled: true, days: 0, time: '00:00' }
  if (expiration === 'programEndDate') return { type: 'programEndDate', enabled: true, days: 0, time: '00:00' }
  if (expiration === 3 * MINUTES_PER_DAY) return { type: '3days', enabled: true, days: 3, time: '00:00' }
  if (expiration === 7 * MINUTES_PER_DAY) return { type: 'week', enabled: true, days: 7, time: '00:00' }
  if (expiration + duration + openSince === MINUTES_PER_DAY)
    return { type: 'endOfDay', enabled: true, days: 0, time: '00:00' }

  const total = expiration + openSince + (duration % MINUTES_PER_DAY)
  const minutes = total % MINUTES_PER_DAY
  const days = Math.floor(total / MINUTES_PER_DAY)
  const time = minutesToTime(minutes)

  return {
    type: 'custom',
    enabled: true,
    days,
    time,
  }
}
